import { z } from 'zod/v4-mini';

import { Schema, cz } from '@/utils/zodUtils.ts';

export type UserType = 'practitioner' | 'patient' | 'admin';

export type User = {
  id: string;
  email: string;
  userType: UserType;
  last_login?: string;
  createdAt: string;
  updatedAt: string;
};

type BaseExtendedUser = {
  id: string;
  email: string;
  lastLogin?: string;
  createdAt: string;
  updatedAt: string;
};

export type PatientUser = BaseExtendedUser & {
  userType: 'patient';
  firstName: string;
  lastName: string;
};

export type PractitionerUser = BaseExtendedUser & {
  userType: 'practitioner';
  firstName: string;
  lastName: string;
  qualification: 'doctor' | 'nurse';
  rpps?: string;
  profilePicture?: { url: string };
};

export type AdminUser = BaseExtendedUser & {
  userType: 'admin';
};

export type ExtendedUser = PatientUser | PractitionerUser | AdminUser;

export const userSchema: Schema<User> = cz.camelCaseObject({
  id: z.string(),
  email: z.string(),
  userType: z.literal(['practitioner', 'patient', 'admin']),
  last_login: cz.optional(z.string()),
  createdAt: z.string(),
  updatedAt: z.string(),
});

export const extendedUserSchema: Schema<ExtendedUser> = cz.camelCase(
  z.intersection(
    z.object({
      id: z.string(),
      email: z.string(),
      last_login: cz.optional(z.string()),
      created_at: z.string(),
      updated_at: z.string(),
    }),
    z.discriminatedUnion('user_type', [
      z.object({
        user_type: z.literal('patient'),
        first_name: z.string(),
        last_name: z.string(),
      }),
      z.object({
        user_type: z.literal('practitioner'),
        first_name: z.string(),
        last_name: z.string(),
        qualification: z.literal(['doctor', 'nurse']),
        rpps: z.string(),
        profile_picture: cz.optional(z.object({ url: z.string() })),
      }),
      z.object({
        user_type: z.literal('admin'),
      }),
    ]),
  ),
);

export const userFormattedName = (
  familyName: string | undefined,
  givenName: string | undefined,
) =>
  familyName && givenName
    ? `${familyName} ${givenName}`.capitalizeAll().trim()
    : '';

export const patientFormattedName = (
  familyName: string | undefined,
  givenName: string | undefined,
) =>
  familyName && givenName
    ? `${givenName} ${familyName}`.capitalizeAll().trim()
    : '';

export const doctorFormattedName = (
  familyName: string | undefined,
  givenName: string | undefined,
) =>
  familyName && givenName
    ? `${givenName.at(0)?.toUpperCase()}. ${familyName.capitalizeAll().trim()}`
    : '';

export const isPatientUser = (user: ExtendedUser): user is PatientUser =>
  user.userType === 'patient';

export const isPractitionerUser = (
  user: ExtendedUser,
): user is PractitionerUser => user.userType === 'practitioner';
